/*

  Rack.ts

  Rack and Tile component

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { Component, m, VnodeChildren } from "./mithril";
import { IBaseGame, IGame, IView, RACK_SIZE } from "./types";
import { ButtonsRecallScramble } from "./buttons";
import { Tile } from "./tile";

interface IDropTargetSquareAttributes {
  view: IView;
  game: IBaseGame;
  coord: string;
}

interface ITileSquareAttributes extends IDropTargetSquareAttributes {
  opponent: boolean;
}

export const TileSquare: Component<ITileSquareAttributes> = {
  // Return a td element that wraps a tile on the board.
  // If the opponent flag is true, we put an '.opp' class on the td
  view: (vnode) => {
    const { view, game, coord } = vnode.attrs;
    if (!game) return undefined;
    // The square contains a tile, so we don't allow dropping a tile on it
    // and don't wish to allow a parent element to accept a drop either.
    // Indicate this by including the not-target class.
    return m("td.not-target",
      {
        id: "sq_" + coord,
        class: game.squareClass(coord),
      },
      m(Tile, { view, game, coord, opponent: false })
    );
  }
};

export const ReviewTileSquare: Component<ITileSquareAttributes> = {
  // Return a td element that wraps an 'inert' tile in a review screen.
  // If the opponent flag is true, we put an '.opp' class on the td
  view: (vnode) => {
    const { game, coord } = vnode.attrs;
    if (!game) return undefined;
    let cls = game.squareClass(coord) || "";
    if (cls)
      cls = "." + cls;
    cls += vnode.attrs.opponent ? ".opp" : ".local";
    return m("td" + cls, { id: "sq_" + coord }, vnode.children);
  }
};

export const DropTargetSquare: Component<IDropTargetSquareAttributes> = {
  // Return a td element that is a target for dropping tiles
  view: (vnode) => {
    const { view, game, coord } = vnode.attrs;
    if (!game) return undefined;
    let cls = game.squareClass(coord) || "";
    if (cls)
      cls = "." + cls;
    // Mark the cell with the 'blinking' class if it is the drop
    // target of a pending blank tile dialog
    if (game.askingForBlank !== null && game.askingForBlank.to == coord)
      cls += ".blinking";
    if (coord == game.startSquare && game.localturn)
      // Unoccupied start square, first move
      cls += ".center";
    return m("td.drop-target" + cls,
      {
        id: "sq_" + coord,
        onclick: (ev: Event) => {
          // If a square is selected (blinking red) and
          // we click on an empty square, move the selected tile
          // to the clicked square
          if (game.selectedSq !== null) {
            ev.stopPropagation();
            game.attemptMove(game.selectedSq, coord);
            game.selectedSq = null;
            (ev.currentTarget as HTMLElement).classList.remove("sel");
            view.updateScale();
            return false;
          }
        },
        onmouseover: (ev: Event) => {
          // If a tile is selected, show a red selection square
          // around this square when the mouse is over it
          if (game.selectedSq !== null)
            (ev.currentTarget as HTMLElement).classList.add("sel");
        },
        onmouseout: (ev: Event) => {
          (ev.currentTarget as HTMLElement).classList.remove("sel");
        }
      },
      vnode.children
    );
  }
};

interface IRackAttributes {
  view: IView;
  game: IBaseGame;
  review: boolean;
}

export const RackTiles: Component<IRackAttributes> = {
  // A rack of 7 tiles (tiles only, no buttons)
  view: (vnode) => {
    const { view, game, review } = vnode.attrs;
    // If review==true, this is a review rack
    // that is not a drop target and whose color reflects the
    // currently shown move.
    if (!game) return undefined;
    const { model } = view;
    let r: VnodeChildren = [];
    const reviewMove = model.reviewMove ?? 0;
    // Avoid flicker when paging through game review screen,
    // as model.reviewMove becomes undefined or 0 while we
    // are fetching the next move to display
    const waiting = review && (reviewMove === 0);
    // If opponent==true, we're showing the opponent's rack
    const opponent = review && (reviewMove > 0) && (reviewMove % 2 === (game as IGame).player);
    for (let i = 1; i <= RACK_SIZE; i++) {
      const coord = `R${i}`;
      if (!waiting && game && (coord in game.tiles)) {
        // We have a tile in this rack slot, but it is a drop target anyway
        if (review) {
          r.push(
            m(ReviewTileSquare, { key: coord, view, game, coord, opponent },
              m(Tile, { view, game, coord, opponent })
            )
          );
        } else {
          r.push(
            m(DropTargetSquare, { key: coord, view, game, coord },
              m(Tile, { view, game, coord, opponent: false })
            )
          );
        }
      }
      else if (review) {
        r.push(m(ReviewTileSquare, { key: coord, view, game, coord, opponent: false }));
      }
      else {
        r.push(m(DropTargetSquare, { key: coord, view, game, coord }));
      }
    }
    return m("table.board", m("tbody", m("tr", r)));
  }
};

export const Rack: Component<IRackAttributes> = {
  // A rack of 7 tiles with buttons (for backward compatibility)
  view: (vnode) => {
    const { view, game, review } = vnode.attrs;
    return m(".rack-row", [
      m(".rack-left", m(ButtonsRecallScramble, { view, game })),
      m(".rack", m(RackTiles, { view, game, review }))
    ]);
  }
};
